import { useEffect } from 'react'

import { TUseApiQuery, useApiData } from '@hozana/api/hooks/useApiData'
import { TQuery } from '@hozana/api/types'
import { usePrevious } from '@hozana/hooks/usePrevious'
import { useSelector } from '@hozana/redux/hooks'

import { NOTIFICATION_TYPE } from 'modules/notification/constants'
import { useNotificationsCount } from 'modules/notification/hooks/useNotificationsCount'
import { TGetNotificationsQueryProps, getNotificationsQuery } from 'modules/notification/queries'
import { selectNotifications } from 'modules/notification/selectors'
import { useUser } from 'modules/user/hooks/useUser'

export type TUseNotificationsProps<NT extends NOTIFICATION_TYPE> = TGetNotificationsQueryProps<NT> &
  Pick<TUseApiQuery<TQuery<number>>, 'cacheOnly' | 'noCache' | 'disabled'>

export const useNotifications = <NT extends NOTIFICATION_TYPE>({
  type,
  objectId,
  objectType,
  readStatus,
  latestId,
  cacheOnly,
  noCache,
  disabled,
}: TUseNotificationsProps<NT>) => {
  const { isLogged } = useUser()
  const notificationsCount = useNotificationsCount(
    [type],
    objectId && objectType ? { objectId, objectType } : undefined,
  )
  const prevNotificationsCount = usePrevious(notificationsCount)

  const [notificationIds, queryDetails] = useApiData(
    () => ({
      query: getNotificationsQuery({ type, objectId, objectType, readStatus, latestId }),
      noCache,
      cacheOnly: cacheOnly && !noCache,
      disabled: disabled || !isLogged,
    }),
    [cacheOnly, disabled, isLogged, latestId, noCache, objectId, objectType, readStatus, type],
  )

  const { refetch } = queryDetails

  useEffect(() => {
    if (prevNotificationsCount !== undefined && notificationsCount !== prevNotificationsCount) {
      refetch()
    }
  }, [notificationsCount, prevNotificationsCount, refetch])

  const notifications = useSelector((state) => selectNotifications<NT>(state, notificationIds))

  return [notifications, queryDetails] as const
}
